import { useEffect, useState } from "react";
import type { ImApi } from "../domain/api";
import type {
  Account,
  DirectoryUser,
  FriendRequests,
  SocialContact,
  UserProfile,
} from "../domain/types";
import { Avatar } from "./Avatar";
import { Icon } from "./Icon";

type ContactsTab = "friends" | "requests" | "discover" | "blocked" | "profile";

interface ContactsData {
  readonly friends: readonly SocialContact[];
  readonly requests: FriendRequests;
  readonly blocked: readonly SocialContact[];
  readonly profile: UserProfile;
}

const EMPTY_REQUESTS: FriendRequests = { incoming: [], outgoing: [] };

/** Full relationship-chain and profile management surface for the web client. */
export function ContactsCenter({
  account,
  api,
  users,
  onClose,
  onStartChat,
  onProfileChanged,
  onError,
}: {
  readonly account: Account;
  readonly api: ImApi;
  readonly users: readonly DirectoryUser[];
  readonly onClose: () => void;
  readonly onStartChat: (user: DirectoryUser) => Promise<void>;
  readonly onProfileChanged: (profile: UserProfile) => void;
  readonly onError: (message: string) => void;
}) {
  const [tab, setTab] = useState<ContactsTab>("friends");
  const [query, setQuery] = useState("");
  const [data, setData] = useState<ContactsData | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);

  async function refresh(): Promise<void> {
    const [friends, requests, blocked, profile] = await Promise.all([
      api.friends(),
      api.friendRequests(),
      api.blockedUsers(),
      api.profile(account.userId),
    ]);
    setData({ friends, requests, blocked, profile });
  }

  useEffect(() => {
    let active = true;
    void refresh().catch((cause: unknown) => {
      if (active) onError(errorMessage(cause));
    });
    return () => { active = false; };
  }, []);

  async function mutate(key: string, action: () => Promise<void>): Promise<void> {
    setBusyKey(key);
    try {
      await action();
      await refresh();
    } catch (cause) {
      onError(errorMessage(cause));
    } finally {
      setBusyKey(null);
    }
  }

  const requests = data?.requests ?? EMPTY_REQUESTS;
  const relationIds = new Set([
    ...(data?.friends ?? []).map((contact) => contact.userId),
    ...requests.incoming.map((contact) => contact.userId),
    ...requests.outgoing.map((contact) => contact.userId),
    ...(data?.blocked ?? []).map((contact) => contact.userId),
  ]);
  const discovered = users.filter((user) => user.userId !== account.userId && !relationIds.has(user.userId));
  const searchValue = query.trim().toLocaleLowerCase();
  const filterUser = (user: DirectoryUser): boolean => searchValue.length === 0 ||
    user.displayName.toLocaleLowerCase().includes(searchValue) ||
    user.userId.toLocaleLowerCase().includes(searchValue) ||
    user.email?.toLocaleLowerCase().includes(searchValue) === true;

  return (
    <div className="modal-backdrop contacts-backdrop" role="presentation" onMouseDown={onClose}>
      <section className="contacts-center" role="dialog" aria-modal="true" aria-label="通讯录"
        onMouseDown={(event) => { event.stopPropagation(); }}>
        <aside className="contacts-navigation">
          <header><Avatar name={account.displayName} url={account.avatarUrl} size={46} />
            <span><strong>{account.displayName}</strong><small>通讯录</small></span></header>
          <nav aria-label="通讯录功能">
            <ContactTabButton active={tab === "friends"} label="我的好友" count={data?.friends.length}
              onClick={() => { setTab("friends"); }} />
            <ContactTabButton active={tab === "requests"} label="好友申请"
              count={requests.incoming.length} alert={requests.incoming.length > 0}
              onClick={() => { setTab("requests"); }} />
            <ContactTabButton active={tab === "discover"} label="发现用户"
              onClick={() => { setTab("discover"); }} />
            <ContactTabButton active={tab === "blocked"} label="黑名单"
              count={data?.blocked.length} onClick={() => { setTab("blocked"); }} />
            <ContactTabButton active={tab === "profile"} label="我的资料"
              onClick={() => { setTab("profile"); }} />
          </nav>
        </aside>
        <main className="contacts-content">
          <header className="contacts-heading">
            <div><p className="eyebrow">CONTACTS</p><h2>{tabTitle(tab)}</h2></div>
            <button className="icon-button" type="button" aria-label="关闭" onClick={onClose}>
              <Icon name="close" />
            </button>
          </header>
          {tab === "profile" ? (
            data === null ? <LoadingContacts /> : (
              <ProfileEditor profile={data.profile} busy={busyKey === "profile"} onSave={(patch) => {
                void mutate("profile", async () => {
                  const profile = await api.updateProfile(patch);
                  onProfileChanged(profile);
                });
              }} />
            )
          ) : (
            <>
              <label className="search-field contacts-search" aria-label="搜索通讯录"><Icon name="search" />
                <input value={query} onChange={(event) => { setQuery(event.target.value); }}
                  placeholder="搜索昵称、账号或邮箱" /></label>
              {data === null ? <LoadingContacts /> : null}
              {data !== null && tab === "friends" ? (
                <ContactList contacts={data.friends.filter((contact) =>
                  contact.profile === null || filterUser(contact.profile)
                )} empty="还没有好友，可以到“发现用户”发送好友申请。"
                  renderActions={(contact) => (
                    <>
                      {contact.profile === null ? null : <button type="button" className="primary compact"
                        onClick={() => { if (contact.profile !== null) void onStartChat(contact.profile); }}>发消息</button>}
                      <button type="button" onClick={() => {
                        const remark = window.prompt("设置好友备注", contact.remark ?? "");
                        if (remark !== null) void mutate(`remark:${contact.userId}`, () =>
                          api.updateFriend(contact.userId, { remark: remark.trim() || null }));
                      }}>备注</button>
                      <button type="button" onClick={() => {
                        if (window.confirm("将该用户加入黑名单？好友关系也会被解除。")) {
                          void mutate(`block:${contact.userId}`, () => api.blockUser(contact.userId));
                        }
                      }}>拉黑</button>
                      <button type="button" className="danger-text" onClick={() => {
                        if (window.confirm("确认删除好友？")) {
                          void mutate(`delete:${contact.userId}`, () => api.deleteFriend(contact.userId));
                        }
                      }}>删除</button>
                    </>
                  )} busyKey={busyKey} />
              ) : null}
              {data !== null && tab === "requests" ? (
                <RequestsList requests={requests} busyKey={busyKey} onRespond={(contact, action) => {
                  void mutate(`${action}:${contact.userId}`, () => api.respondFriend(contact.userId, action));
                }} />
              ) : null}
              {data !== null && tab === "discover" ? (
                <DirectoryList users={discovered.filter(filterUser)} busyKey={busyKey} onRequest={(user) => {
                  const message = window.prompt("申请留言（可选）", `你好，我是${account.displayName}`);
                  if (message !== null) void mutate(`request:${user.userId}`, () =>
                    api.requestFriend(user.userId, message.trim() || undefined));
                }} />
              ) : null}
              {data !== null && tab === "blocked" ? (
                <ContactList contacts={data.blocked.filter((contact) =>
                  contact.profile === null || filterUser(contact.profile)
                )} empty="黑名单为空。" busyKey={busyKey} renderActions={(contact) => (
                  <button type="button" onClick={() => { void mutate(
                    `unblock:${contact.userId}`,
                    () => api.unblockUser(contact.userId),
                  ); }}>移出黑名单</button>
                )} />
              ) : null}
            </>
          )}
        </main>
      </section>
    </div>
  );
}

function ContactTabButton({ active, label, count, alert, onClick }: {
  readonly active: boolean;
  readonly label: string;
  readonly count?: number;
  readonly alert?: boolean;
  readonly onClick: () => void;
}) {
  return <button type="button" className={active ? "active" : ""} onClick={onClick}>
    <span>{label}</span>{count === undefined || count === 0 ? null : <b className={alert ? "alert" : ""}>{count}</b>}
  </button>;
}

function ContactList({ contacts, empty, busyKey, renderActions }: {
  readonly contacts: readonly SocialContact[];
  readonly empty: string;
  readonly busyKey: string | null;
  readonly renderActions: (contact: SocialContact) => React.ReactNode;
}) {
  if (contacts.length === 0) return <p className="contacts-empty">{empty}</p>;
  return <div className="contact-management-list">{contacts.map((contact) => (
    <article key={contact.userId} className={busyKey?.endsWith(contact.userId) === true ? "busy" : ""}>
      <Avatar name={contact.profile?.displayName ?? contact.userId} url={contact.profile?.avatarUrl} size={44} />
      <span><strong>{contact.remark ?? contact.profile?.displayName ?? contact.userId}</strong>
        <small>{contact.profile?.bio ?? contact.profile?.email ?? contact.userId}</small></span>
      <div>{renderActions(contact)}</div>
    </article>
  ))}</div>;
}

function DirectoryList({ users, busyKey, onRequest }: {
  readonly users: readonly DirectoryUser[];
  readonly busyKey: string | null;
  readonly onRequest: (user: DirectoryUser) => void;
}) {
  if (users.length === 0) return <p className="contacts-empty">没有匹配的新用户。</p>;
  return <div className="contact-management-list">{users.map((user) => (
    <article key={user.userId} className={busyKey === `request:${user.userId}` ? "busy" : ""}>
      <Avatar name={user.displayName} url={user.avatarUrl} size={44} />
      <span><strong>{user.displayName}</strong><small>{user.email ?? user.userId}</small></span>
      <div><button className="primary compact" type="button" onClick={() => { onRequest(user); }}>添加好友</button></div>
    </article>
  ))}</div>;
}

function RequestsList({ requests, busyKey, onRespond }: {
  readonly requests: FriendRequests;
  readonly busyKey: string | null;
  readonly onRespond: (contact: SocialContact, action: "accept" | "decline") => void;
}) {
  if (requests.incoming.length === 0 && requests.outgoing.length === 0) {
    return <p className="contacts-empty">暂无好友申请。</p>;
  }
  return <div className="request-sections">
    {requests.incoming.length === 0 ? null : <section><h3>收到的申请</h3>
      <ContactList contacts={requests.incoming} empty="" busyKey={busyKey} renderActions={(contact) => <>
        <button className="primary compact" type="button" onClick={() => { onRespond(contact, "accept"); }}>接受</button>
        <button type="button" onClick={() => { onRespond(contact, "decline"); }}>拒绝</button>
      </>} /></section>}
    {requests.outgoing.length === 0 ? null : <section><h3>发出的申请</h3>
      <ContactList contacts={requests.outgoing} empty="" busyKey={busyKey}
        renderActions={() => <span className="pending-label">等待验证</span>} /></section>}
  </div>;
}

function ProfileEditor({ profile, busy, onSave }: {
  readonly profile: UserProfile;
  readonly busy: boolean;
  readonly onSave: (patch: Parameters<ImApi["updateProfile"]>[0]) => void;
}) {
  const [displayName, setDisplayName] = useState(profile.displayName);
  const [avatarUrl, setAvatarUrl] = useState(profile.avatarUrl ?? "");
  const [bio, setBio] = useState(profile.bio ?? "");
  const [location, setLocation] = useState(profile.location ?? "");
  const [gender, setGender] = useState(profile.gender);
  const [birthday, setBirthday] = useState(profile.birthday ?? "");
  const [friendPolicy, setFriendPolicy] = useState(profile.friendPolicy);
  const [messagePolicy, setMessagePolicy] = useState(profile.messagePolicy);
  return <form className="profile-form" onSubmit={(event) => {
    event.preventDefault();
    onSave({
      displayName: displayName.trim(),
      avatarUrl: avatarUrl.trim() || null,
      bio: bio.trim() || null,
      location: location.trim() || null,
      gender,
      birthday: birthday || null,
      friendPolicy,
      messagePolicy,
    });
  }}>
    <div className="profile-preview"><Avatar name={displayName || profile.userId} url={avatarUrl || null} size={72} />
      <span><strong>{displayName || profile.userId}</strong><small>{profile.userId}</small></span></div>
    <div className="profile-grid">
      <label><span>昵称</span><input required maxLength={64} value={displayName}
        onChange={(event) => { setDisplayName(event.target.value); }} /></label>
      <label><span>头像 HTTPS 地址</span><input type="url" value={avatarUrl}
        onChange={(event) => { setAvatarUrl(event.target.value); }} /></label>
      <label className="wide"><span>个性签名</span><textarea maxLength={500} value={bio}
        onChange={(event) => { setBio(event.target.value); }} /></label>
      <label><span>地区</span><input maxLength={128} value={location}
        onChange={(event) => { setLocation(event.target.value); }} /></label>
      <label><span>生日</span><input type="date" value={birthday}
        onChange={(event) => { setBirthday(event.target.value); }} /></label>
      <label><span>性别</span><select value={gender} onChange={(event) => {
        setGender(event.target.value as UserProfile["gender"]);
      }}><option value="unspecified">未设置</option><option value="female">女</option>
        <option value="male">男</option><option value="other">其他</option></select></label>
      <label><span>加好友方式</span><select value={friendPolicy} onChange={(event) => {
        setFriendPolicy(event.target.value as UserProfile["friendPolicy"]);
      }}><option value="approval">需要验证</option><option value="anyone">自动通过</option>
        <option value="none">禁止申请</option></select></label>
      <label><span>私聊权限</span><select value={messagePolicy} onChange={(event) => {
        setMessagePolicy(event.target.value as UserProfile["messagePolicy"]);
      }}><option value="anyone">所有人</option><option value="friends">仅好友</option></select></label>
    </div>
    <footer><button className="primary" type="submit" disabled={busy || displayName.trim().length === 0}>
      {busy ? "保存中…" : "保存资料"}</button></footer>
  </form>;
}

function LoadingContacts() {
  return <div className="contacts-loading"><span className="spinner dark" />正在同步通讯录…</div>;
}

function tabTitle(tab: ContactsTab): string {
  if (tab === "friends") return "我的好友";
  if (tab === "requests") return "好友申请";
  if (tab === "discover") return "发现用户";
  if (tab === "blocked") return "黑名单";
  return "我的资料";
}

function errorMessage(cause: unknown): string {
  return cause instanceof Error ? cause.message : "通讯录操作失败";
}
